import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Building2, Check, Copy, Download, FileText, Plus, X as XIcon } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { Button, Skeleton } from "@finora/ui";
import { formatMoney } from "@finora/config";
import type { CompanyAdminDto, ContractDto, ContractDurationOption, ContractSlaLevel } from "@finora/types";
import { api } from "../../lib/api";
import { KasaPaketiAdmin } from "./KasaPaketiAdmin";
import { LpfrDevicesTab } from "./LpfrDevicesTab";

type Tab = "overview" | "moduli" | "usage" | "obrada-izvoda" | "finora-mobile" | "lpfr" | "ugovor" | "fakture" | "audit";

const STATUS_OPTIONS = ["ACTIVE", "DISABLED", "TRIAL", "SUSPENDED"];
const BILLING_TYPE_OPTIONS = ["FREE", "PER_TRANSACTION", "MONTHLY", "PER_DOCUMENT", "PER_API_CALL", "PER_UNIT"];

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<Tab>("overview");

  const { data: companies } = useQuery({
    queryKey: ["super-admin-companies"],
    queryFn: () => api.listAllCompanies(),
  });
  const company = companies?.find((c) => c.id === id);

  const { data: stats } = useQuery({
    queryKey: ["company-stats", id],
    queryFn: () => api.getCompanyStats(id!),
    enabled: !!id,
  });

  if (!id) return null;

  return (
    <div className="space-y-4">
      <Link to="/finora-admin" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-4 w-4" aria-hidden /> Nazad na listu firmi
      </Link>

      <div className="flex items-center gap-3 rounded-xl border border-border bg-surface p-5 shadow-subtle">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-light text-brand">
          <Building2 className="h-6 w-6" aria-hidden />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-slate-900">{company?.name ?? "..."}</h1>
          <p className="text-sm text-slate-500">
            PIB: {company?.taxId ?? "—"} · {company?.isActive ? "Aktivan" : "Suspendovan"}
          </p>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "Kupci", value: stats.customerCount },
            { label: "Ponude", value: stats.quoteCount },
            { label: "Fakture", value: stats.invoiceCount },
            { label: "Fiskalizovano", value: stats.fiscalizedCount },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
              <p className="text-xs text-slate-400">{s.label}</p>
              <p className="text-xl font-semibold text-slate-900">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-1 border-b border-border">
        {(
          [
            { key: "overview", label: "Overview" },
            { key: "moduli", label: "Moduli" },
            { key: "usage", label: "Potrošnja" },
            { key: "obrada-izvoda", label: "Obrada izvoda" },
            { key: "finora-mobile", label: "HAFiskal kasa" },
            { key: "lpfr", label: "L-PFR" },
            { key: "ugovor", label: "Ugovor" },
            { key: "fakture", label: "Fakture" },
            { key: "audit", label: "Audit log" },
          ] as { key: Tab; label: string }[]
        ).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`border-b-2 px-4 py-2 text-sm font-medium ${
              tab === t.key ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab companyId={id} company={company} />}
      {tab === "moduli" && <ModuliTab companyId={id} />}
      {tab === "usage" && <UsageTab companyId={id} />}
      {tab === "obrada-izvoda" && <ObradaIzvodaTab companyId={id} />}
      {tab === "finora-mobile" && <FinoraMobileTab companyId={id} />}
      {tab === "ugovor" && <UgovorTab companyId={id} />}
      {tab === "fakture" && <FaktureTab companyId={id} />}
      {tab === "lpfr" && <LpfrTab companyId={id} />}
      {tab === "audit" && <AuditLogTab companyId={id} />}
    </div>
  );
}

function AprSyncButton({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selFields, setSelFields] = useState<Set<string>>(new Set());
  const [selAcc, setSelAcc] = useState<Set<string>>(new Set());
  const [msg, setMsg] = useState<string | null>(null);

  const check = useMutation({
    mutationFn: () => api.previewCompanyAprSync(companyId),
    onSuccess: (d) => {
      setSelFields(new Set(d.fields.filter((f) => f.status === "FILL").map((f) => f.key)));
      setSelAcc(new Set(d.accounts.filter((a) => a.isNew).map((a) => a.accountNumber)));
      setMsg(null);
      setOpen(true);
    },
    onError: (e) => alert(e instanceof ApiError ? e.message : "Provera nije uspela."),
  });
  const apply = useMutation({
    mutationFn: () => api.applyCompanyAprSync(companyId, { fields: [...selFields], accounts: [...selAcc] }),
    onSuccess: (r) => {
      setMsg(`Sačuvano: ${r.updatedFields} polja, ${r.addedAccounts} računa.`);
      queryClient.invalidateQueries({ queryKey: ["super-admin-companies"] });
      queryClient.invalidateQueries({ queryKey: ["admin-company-users", companyId] });
      queryClient.invalidateQueries({ queryKey: ["admin-audit-log", "Company", companyId] });
    },
    onError: (e) => alert(e instanceof ApiError ? e.message : "Čuvanje nije uspelo."),
  });
  const d = check.data;
  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, k: string) => {
    const n = new Set(set);
    if (n.has(k)) n.delete(k);
    else n.add(k);
    setter(n);
  };
  const badge: Record<string, string> = { SAME: "isto", FILL: "prazno → popuni", DIFF: "različito", NONE: "nema u registru" };

  return (
    <>
      <Button size="sm" variant="secondary" onClick={() => check.mutate()} disabled={check.isPending}>
        {check.isPending ? "Proveravam..." : "Sinhronizuj sa APR"}
      </Button>
      {open && d && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
            <div className="mb-1 text-base font-semibold text-slate-800">Sinhronizacija sa APR / NBS</div>
            <div className="mb-3 text-xs text-slate-500">
              PIB {d.pib} · u registru: {d.foundName || "—"}
              {d.activityName ? ` · ${d.activityName}` : ""}
              {d.owner ? ` · ${d.owner}` : ""}
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400">
                  <th className="w-8" />
                  <th>Polje</th>
                  <th>U HAFiskalu</th>
                  <th>U registru</th>
                </tr>
              </thead>
              <tbody>
                {d.fields.map((f) => (
                  <tr key={f.key} className="border-t border-slate-100">
                    <td>
                      <input
                        type="checkbox"
                        disabled={!f.found || f.status === "SAME"}
                        checked={selFields.has(f.key)}
                        onChange={() => toggle(selFields, setSelFields, f.key)}
                      />
                    </td>
                    <td className="py-1.5">{f.label}</td>
                    <td className="text-slate-600">{f.current || "—"}</td>
                    <td className={f.status === "DIFF" ? "text-amber-700" : "text-slate-700"}>
                      {f.found || "—"} <span className="text-[10px] text-slate-400">{badge[f.status]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mb-1 mt-4 text-xs font-semibold text-slate-600">Tekući računi</div>
            {d.accounts.length === 0 ? (
              <div className="text-sm text-slate-400">Registar nije vratio račune.</div>
            ) : (
              d.accounts.map((a) => (
                <label key={a.accountNumber} className="flex items-center gap-2 border-t border-slate-100 py-1.5 text-sm">
                  <input
                    type="checkbox"
                    disabled={!a.isNew}
                    checked={selAcc.has(a.accountNumber)}
                    onChange={() => toggle(selAcc, setSelAcc, a.accountNumber)}
                  />
                  <span>
                    {a.accountNumber}
                    {a.bankName ? ` (${a.bankName})` : ""}
                  </span>
                  <span className="text-[10px] text-slate-400">{a.isNew ? "novi" : "već postoji"}</span>
                </label>
              ))
            )}
            {msg && <div className="mt-3 text-sm text-emerald-700">{msg}</div>}
            <div className="mt-4 flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>
                Zatvori
              </Button>
              <Button size="sm" onClick={() => apply.mutate()} disabled={apply.isPending || (selFields.size === 0 && selAcc.size === 0)}>
                {apply.isPending ? "Čuvam..." : "Primeni izabrano"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function AuditLogTab({ companyId }: { companyId: string }) {
  const { data: logs, isLoading } = useQuery({
    queryKey: ["admin-audit-log", "Company", companyId],
    queryFn: () => api.listAdminAuditLog("Company", companyId),
  });
  return (
      <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Audit log</h2>
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : !logs || logs.length === 0 ? (
          <p className="text-sm text-slate-400">Još nema zabeleženih izmena za ovu firmu.</p>
        ) : (
          <div className="space-y-2">
            {logs.map((log) => (
              <div key={log.id} className="rounded-lg bg-app-bg px-3 py-2 text-sm">
                <p className="font-medium text-slate-700">{log.action}</p>
                <p className="text-xs text-slate-400">{new Date(log.createdAt).toLocaleString("sr-RS")}</p>
              </div>
            ))}
          </div>
        )}
      </div>
  );
}

function LpfrTab({ companyId }: { companyId: string }) {
  return (
    <div className="space-y-4">
      <KasaPaketiAdmin companyId={companyId} />
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Aktivni L-PFR uređaji firme</h2>
        <p className="mb-2 text-xs text-slate-400">Uređaji koji su se javili serveru sa licencom ove firme.</p>
        <LpfrDevicesTab companyId={companyId} />
      </div>
    </div>
  );
}

function OverviewTab({ companyId, company }: { companyId: string; company?: CompanyAdminDto }) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-slate-800">Podaci firme</h2>
          <AprSyncButton companyId={companyId} />
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs text-slate-400">PIB</dt>
            <dd className="text-slate-700">{company?.taxId || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-400">Matični broj</dt>
            <dd className="text-slate-700">{company?.registrationNumber || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-400">Tekući račun(i)</dt>
            <dd className="text-slate-700">
              {company?.bankAccounts && company.bankAccounts.length > 0
                ? company.bankAccounts.map((b) => (
                    <div key={b.accountNumber}>
                      {b.accountNumber}
                      {b.bankName ? ` (${b.bankName})` : ""}
                    </div>
                  ))
                : "—"}
            </dd>
          </div>
        </dl>
      </div>
      <CompanyProfileSection companyId={companyId} company={company} />
      <CompanyUsersSection companyId={companyId} />
    </div>
  );
}

/**
 * "Moj profil" (ulica/broj/poštanski broj/mesto/telefon) - super admin
 * pregled/izmena istih polja koje tenant korisnik uređuje u Podešavanjima
 * (Firma tab) - PATCH /super-admin/companies/:id/profile, 30.09.2026.
 */
function CompanyProfileSection({ companyId, company }: { companyId: string; company?: CompanyAdminDto }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [street, setStreet] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [phone, setPhone] = useState("");

  function startEditing() {
    setStreet(company?.street ?? "");
    setHouseNumber(company?.houseNumber ?? "");
    setPostalCode(company?.postalCode ?? "");
    setCity(company?.city ?? "");
    setPhone(company?.phone ?? "");
    setPullMsg(null);
    setEditing(true);
  }

  const [pullMsg, setPullMsg] = useState<string | null>(null);
  const pull = useMutation({
    mutationFn: () => api.lookupCompanyByPib(company?.taxId ?? ""),
    onSuccess: (r) => {
      if (r.street) setStreet(r.street);
      if (r.houseNumber) setHouseNumber(r.houseNumber);
      if (r.postalCode) setPostalCode(r.postalCode);
      if (r.city) setCity(r.city);
      setPullMsg(r.street || r.city ? "Podaci povučeni — proveri i sačuvaj." : "Za ovaj PIB nema podataka o adresi.");
    },
    onError: (e) => setPullMsg(e instanceof ApiError ? e.response.message : "Povlačenje podataka nije uspelo."),
  });

  const save = useMutation({
    mutationFn: () =>
      api.updateCompanyProfileAdmin(companyId, { street, houseNumber, postalCode, city, phone }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["super-admin-companies"] });
      setEditing(false);
    },
  });

  return (
    <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-800">Moj profil (adresa i kontakt firme)</h2>
        {!editing && (
          <Button size="sm" variant="secondary" onClick={startEditing}>
            Izmeni
          </Button>
        )}
      </div>

      {editing ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Button size="sm" variant="secondary" disabled={pull.isPending || !company?.taxId} onClick={() => pull.mutate()}>
              {pull.isPending ? "Povlačim…" : "Povuci podatke"}
            </Button>
            {pullMsg && <span className="text-xs text-slate-500">{pullMsg}</span>}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <div className="sm:col-span-3">
              <label className="mb-1 block text-xs font-medium text-slate-500">Ulica</label>
              <input
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                className="h-9 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Broj</label>
              <input
                value={houseNumber}
                onChange={(e) => setHouseNumber(e.target.value)}
                className="h-9 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Poštanski broj</label>
              <input
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className="h-9 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Mesto</label>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="h-9 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Telefon</label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-9 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
          </div>

          {save.isError && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {save.error instanceof ApiError ? save.error.response.message : "Čuvanje nije uspelo."}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
              Otkaži
            </Button>
            <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
              Sačuvaj
            </Button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-xs text-slate-400">Ulica i broj</dt>
            <dd className="text-slate-700">
              {company?.street || company?.houseNumber
                ? `${company?.street ?? ""} ${company?.houseNumber ?? ""}`.trim()
                : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-400">Poštanski broj</dt>
            <dd className="text-slate-700">{company?.postalCode || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-400">Mesto</dt>
            <dd className="text-slate-700">{company?.city || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-400">Telefon</dt>
            <dd className="text-slate-700">{company?.phone || "—"}</dd>
          </div>
        </dl>
      )}
    </div>
  );
}

const inputCls = "h-9 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";

/** Korisnici firme: pregled, izmena (ime/email/lozinka/status) i slanje reset lozinke. */
function CompanyUsersSection({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-company-users", companyId],
    queryFn: () => api.listCompanyUsersAdmin(companyId),
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const errText = (e: unknown, fb: string) => (e instanceof ApiError ? e.response.message : fb);

  const save = useMutation({
    mutationFn: (userId: string) =>
      api.updateCompanyUserAdmin(companyId, userId, {
        fullName,
        email,
        isActive,
        ...(password ? { password } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-company-users", companyId] });
      setEditId(null);
      setMsg({ ok: true, text: "Korisnik sačuvan." });
    },
    onError: (e) => setMsg({ ok: false, text: errText(e, "Čuvanje nije uspelo.") }),
  });

  const reset = useMutation({
    mutationFn: (userId: string) => api.sendCompanyUserPasswordResetAdmin(companyId, userId),
    onSuccess: (r) =>
      setMsg({
        ok: true,
        text: r.kind === "reset" ? "Email za reset lozinke je poslat." : "Korisnik nije verifikovan — poslat mu je email za verifikaciju.",
      }),
    onError: (e) => setMsg({ ok: false, text: errText(e, "Slanje nije uspelo.") }),
  });

  function startEdit(u: NonNullable<typeof data>[number]) {
    setEditId(u.id);
    setFullName(u.fullName);
    setEmail(u.email);
    setPassword("");
    setIsActive(u.isActive);
    setMsg(null);
  }

  return (
    <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
      <h2 className="mb-3 text-sm font-semibold text-slate-800">Korisnik firme (nalog)</h2>
      {msg && (
        <p className={`mb-3 rounded-lg px-3 py-2 text-sm ${msg.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
          {msg.text}
        </p>
      )}
      {isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : !data || data.length === 0 ? (
        <p className="text-sm text-slate-400">Firma nema korisnika.</p>
      ) : (
        <div className="space-y-3">
          {data.map((u) =>
            editId === u.id ? (
              <div key={u.id} className="space-y-3 rounded-lg bg-app-bg p-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500">Ime i prezime</label>
                    <input value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500">Email</label>
                    <input value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500">Nova lozinka (prazno = bez promene)</label>
                    <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
                  </div>
                  <label className="flex items-end gap-2 pb-2 text-sm text-slate-700">
                    <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                    Aktivan
                  </label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setEditId(null)}>
                    Otkaži
                  </Button>
                  <Button size="sm" disabled={save.isPending} onClick={() => save.mutate(u.id)}>
                    Sačuvaj
                  </Button>
                </div>
              </div>
            ) : (
              <div key={u.id} className="rounded-lg bg-app-bg p-3">
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-5">
                  <div>
                    <dt className="text-xs text-slate-400">Ime i prezime</dt>
                    <dd className="text-slate-700">{u.fullName}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-400">Email</dt>
                    <dd className="break-all text-slate-700">{u.email}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-400">Firma</dt>
                    <dd className="text-slate-700">{u.companyName}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-400">Registrovan</dt>
                    <dd className="text-slate-700">{new Date(u.createdAt).toLocaleDateString("sr-RS")}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-400">Status</dt>
                    <dd className="text-slate-700">
                      {u.isActive ? "Aktivan" : "Neaktivan"}
                      {!u.verified && " · nije verifikovan"}
                    </dd>
                  </div>
                </dl>
                <div className="mt-3 flex justify-end gap-2">
                  <Button size="sm" variant="secondary" disabled={reset.isPending} onClick={() => reset.mutate(u.id)}>
                    Pošalji reset lozinke
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => startEdit(u)}>
                    Izmeni
                  </Button>
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}

/** Fakture ove firme (kartice Plaćeno / Neplaćeno + lista). */
function FaktureTab({ companyId }: { companyId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-company-invoices", companyId],
    queryFn: () => api.listCompanyInvoicesAdmin(companyId),
  });
  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (!data) return <p className="text-sm text-slate-400">Nema podataka.</p>;
  const { summary, invoices } = data;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
          <p className="text-xs text-slate-400">Plaćeno ({summary.paidCount})</p>
          <p className="text-xl font-semibold text-green-700">{formatMoney(summary.paidAmount, summary.currency)}</p>
        </div>
        <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
          <p className="text-xs text-slate-400">Neplaćeno ({summary.unpaidCount})</p>
          <p className="text-xl font-semibold text-red-700">{formatMoney(summary.unpaidAmount, summary.currency)}</p>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-subtle">
        {invoices.length === 0 ? (
          <p className="p-5 text-sm text-slate-400">Još nema faktura za ovu firmu.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-slate-400">
                <th className="px-4 py-2">Broj</th>
                <th className="px-4 py-2">Datum</th>
                <th className="px-4 py-2">Valuta plaćanja</th>
                <th className="px-4 py-2 text-right">Iznos</th>
                <th className="px-4 py-2 text-right">Dugovanje</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 font-medium text-slate-700">{inv.documentNumber}</td>
                  <td className="px-4 py-2">{new Date(inv.issueDate).toLocaleDateString("sr-RS")}</td>
                  <td className="px-4 py-2">{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString("sr-RS") : "—"}</td>
                  <td className="px-4 py-2 text-right">{formatMoney(inv.grossAmount, inv.currency)}</td>
                  <td className="px-4 py-2 text-right">{formatMoney(inv.dueAmount, inv.currency)}</td>
                  <td className="px-4 py-2">
                    <span className={inv.isPaid ? "text-green-700" : "text-red-700"}>{inv.isPaid ? "Plaćeno" : "Neplaćeno"}</span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    {inv.publicLinkToken && (
                      <a href={`/pregled/racun/${inv.publicLinkToken}`} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                        Otvori
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const SUBSCRIPTION_STATUS_OPTIONS = ["TRIAL", "ACTIVE", "PAST_DUE", "CANCELLED", "EXPIRED", "SUSPENDED"];

/**
 * Dodela paketa (Subscription) firmi - "novi način" (vidi razgovor "prebaci
 * promene paketa u podešavanjima firme na novi način"). Do sada Super Admin
 * panel nije imao NIKAKVU direktnu kontrolu nad Subscription redom - samo
 * firma sama, plaćanjem ponude kroz "Moj paket" -> "Promeni paket". Ova
 * sekcija dodaje tačno to: izbor paketa i status pretplate, direktno, bez
 * fakture. Namerno ODVOJENO od Moduli grida ispod (koji ostaje nepromenjen)
 * - grid i dalje ispravno pokreće stvarnu naplatu (UsageTrackingService) i
 * push/print-agent provere kroz CompanyModule, dodela paketa ovde samo
 * dodatno aktivira module iz paketa na isti način kao plaćena ponuda (vidi
 * BillingAdminService.setCompanySubscription).
 */
function PackageSubscriptionSection({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["company-subscription", companyId],
    queryFn: () => api.getCompanySubscriptionAdmin(companyId),
  });

  const [selectedPackageId, setSelectedPackageId] = useState("");
  useEffect(() => {
    setSelectedPackageId(data?.subscription?.packageId ?? "");
  }, [data?.subscription?.packageId]);

  const assign = useMutation({
    mutationFn: (packageId: string) => api.setCompanySubscriptionAdmin(companyId, packageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-subscription", companyId] });
      queryClient.invalidateQueries({ queryKey: ["company-modules", companyId] });
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: string) => api.setCompanySubscriptionStatusAdmin(companyId, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["company-subscription", companyId] }),
  });

  if (isLoading) return <Skeleton className="h-16 w-full" />;

  const current = data?.subscription;

  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <h2 className="mb-3 text-sm font-semibold text-slate-800">Paket (Subscription)</h2>
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium text-slate-700">Paket:</label>
        <select
          value={selectedPackageId}
          onChange={(e) => {
            setSelectedPackageId(e.target.value);
            if (e.target.value) assign.mutate(e.target.value);
          }}
          className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
        >
          <option value="">(bez paketa)</option>
          {data?.packages.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {p.price} RSD / {p.billingPeriod === "YEARLY" ? "god." : "mes."}
            </option>
          ))}
        </select>

        {current && (
          <>
            <label className="text-sm font-medium text-slate-700">Status:</label>
            <select
              value={current.status}
              onChange={(e) => setStatus.mutate(e.target.value)}
              className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            >
              {SUBSCRIPTION_STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <span className="text-xs text-slate-400">
              period: {new Date(current.currentPeriodStart).toLocaleDateString("sr-RS")} –{" "}
              {new Date(current.currentPeriodEnd).toLocaleDateString("sr-RS")}
              {current.trialEndsAt && ` · trial do ${new Date(current.trialEndsAt).toLocaleDateString("sr-RS")}`}
            </span>
          </>
        )}
        {!current && <span className="text-xs text-slate-400">Firma nema dodeljen paket.</span>}
      </div>
      <p className="mt-2 text-xs text-slate-400">
        Dodela paketa ovde je istog efekta kao plaćena ponuda „Promeni paket" (aktivira module iz paketa ispod u
        Moduli gridu), samo bez fakture.
      </p>
    </div>
  );
}

function ModuliTab({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const { data: modules, isLoading } = useQuery({
    queryKey: ["company-modules", companyId],
    queryFn: () => api.getCompanyModules(companyId),
  });
  const { data: groups } = useQuery({ queryKey: ["module-groups"], queryFn: () => api.listModuleGroups() });
  const { data: companies } = useQuery({ queryKey: ["super-admin-companies"], queryFn: () => api.listAllCompanies() });
  const currentBillingGroupId = companies?.find((c) => c.id === companyId)?.billingGroupId ?? "";

  // Kontrolisano polje (ne key-remount trik) - selectedGroupId je izvor
  // istine za PRIKAZ, sinhronizuje se sa serverom kad stignu sveži podaci,
  // ali se odmah menja i lokalno na klik (optimistički) da se ne čeka
  // round-trip da bi se videla promena.
  const [selectedGroupId, setSelectedGroupId] = useState(currentBillingGroupId);
  useEffect(() => {
    setSelectedGroupId(currentBillingGroupId);
  }, [currentBillingGroupId]);

  const update = useMutation({
    mutationFn: (payload: {
      moduleId: string;
      status: string;
      overrideBillingType?: string | null;
      overridePrice?: string | null;
      licenseCount?: number | null;
    }) => api.setCompanyModule(companyId, payload.moduleId, payload),
    // Status/tip naplate/cena/broj licenci treba da se promene ODMAH u
    // tabeli, bez osvežavanja strane (vidi razgovor "ide on change a ne da
    // mora da refresuje stranu"). Backend sada vraća AŽURIRAN red u istom
    // obliku kao lista (BillingAdminService.toModuleDto), pa ga upisujemo
    // direktno u react-query keš (setQueryData) - nema refetch round-trip-a
    // koji čeka mrežu, i nema pun page reload.
    onSuccess: (updatedModule) => {
      queryClient.setQueryData<typeof modules>(["company-modules", companyId], (old) =>
        old?.map((m) => (m.moduleId === updatedModule.moduleId ? updatedModule : m)),
      );
    },
  });

  const setGroup = useMutation({
    mutationFn: (groupId: string) => api.setCompanyBillingGroup(companyId, groupId || null),
    onSuccess: () => {
      // Nakon više pokušaja (invalidateQueries, key-remount, eksplicitan
      // refetch) i dalje se u ovom okruženju dešavalo da prikaz ostane
      // stara vrednost dok se stranica ručno ne osveži - iz nepoznatog
      // razloga React Query refetch ovde nije pouzdano prikazivao sveže
      // podatke. Pun reload GARANTOVANO radi (potvrđeno - ručno osvežavanje
      // uvek pokazuje tačnu vrednost), pa se ovde koristi direktno umesto
      // daljeg nagađanja.
      window.location.reload();
    },
  });

  if (isLoading) return <Skeleton className="h-40 w-full" />;

  return (
    <div className="space-y-4">
      <PackageSubscriptionSection companyId={companyId} />

      <div className="flex items-center gap-3 rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <label className="text-sm font-medium text-slate-700">Billing grupa (cenovnik):</label>
        <select
          value={selectedGroupId}
          onChange={(e) => {
            setSelectedGroupId(e.target.value);
            setGroup.mutate(e.target.value);
          }}
          className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
        >
          <option value="">(bez grupe - koriste se podrazumevane cene)</option>
          {groups?.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        <div className="border-b border-border px-4 py-2">
          <h2 className="text-sm font-semibold text-slate-800">Moduli (ručna aktivacija i naplata po modulu)</h2>
          <p className="text-xs text-slate-400">
            Ovde se aktivacija za naplatu ne menja dodelom paketa iznad — paket automatski aktivira svoje module, a
            ovaj grid služi za ručne dodatke, override cene i broj licenci.
          </p>
        </div>
        <table>
          <thead>
            <tr>
              <th>Modul</th>
              <th>Status</th>
              <th>Tip naplate</th>
              <th>Cena (override)</th>
              <th>Efektivna cena</th>
              <th>Broj licenci</th>
            </tr>
          </thead>
          <tbody>
            {modules?.map((m) => (
              <tr key={m.moduleId}>
                <td className="font-medium text-slate-800">
                  {m.parentKey && <span className="text-slate-300"> ↳ </span>}
                  {m.moduleName}
                </td>
                <td>
                  <select
                    value={m.status}
                    onChange={(e) =>
                      update.mutate({ moduleId: m.moduleId, status: e.target.value })
                    }
                    className="h-8 rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    value={m.overrideBillingType ?? ""}
                    onChange={(e) =>
                      update.mutate({
                        moduleId: m.moduleId,
                        status: m.status,
                        overrideBillingType: e.target.value || null,
                        overridePrice: m.overridePrice,
                      })
                    }
                    className="h-8 rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none"
                  >
                    <option value="">(iz grupe/default)</option>
                    {BILLING_TYPE_OPTIONS.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    key={`${m.moduleId}-${m.overridePrice ?? ""}`}
                    defaultValue={m.overridePrice ?? ""}
                    placeholder="—"
                    onBlur={(e) =>
                      update.mutate({
                        moduleId: m.moduleId,
                        status: m.status,
                        overrideBillingType: m.overrideBillingType,
                        overridePrice: e.target.value || null,
                      })
                    }
                    className="h-8 w-24 rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none"
                  />
                </td>
                <td className="text-slate-500">
                  {m.effectiveBillingType === "FREE" ? "Besplatno" : `${m.effectivePrice} RSD`}
                </td>
                <td>
                  {m.moduleKey === "finora_mobile" ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() =>
                          update.mutate({
                            moduleId: m.moduleId,
                            status: m.status,
                            licenseCount: Math.max(0, (m.licenseCount ?? 0) - 1),
                          })
                        }
                        className="h-6 w-6 rounded border border-border text-xs hover:bg-app-bg"
                      >
                        −
                      </button>
                      <span className="w-6 text-center text-sm">{m.licenseCount ?? 0}</span>
                      <button
                        onClick={() =>
                          update.mutate({
                            moduleId: m.moduleId,
                            status: m.status,
                            licenseCount: (m.licenseCount ?? 0) + 1,
                          })
                        }
                        className="h-6 w-6 rounded border border-border text-xs hover:bg-app-bg"
                      >
                        +
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-300">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UsageTab({ companyId }: { companyId: string }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { data: usage, isLoading } = useQuery({
    queryKey: ["company-usage", companyId, from, to],
    queryFn: () => api.getCompanyUsage(companyId, from || undefined, to || undefined),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2 rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <div>
          <label className="mb-1 block text-xs text-slate-500">Od</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">Do</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : usage ? (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
              <p className="text-xs text-slate-400">Naplatnih jedinica</p>
              <p className="text-xl font-semibold text-slate-900">{usage.totalBillableUnits}</p>
            </div>
            <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
              <p className="text-xs text-slate-400">Ukupan trošak</p>
              <p className="text-xl font-semibold text-slate-900">{usage.totalCost.toFixed(2)} RSD</p>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-surface shadow-subtle">
            <table>
              <thead>
                <tr>
                  <th>Modul</th>
                  <th>Broj događaja</th>
                  <th>Ukupno</th>
                </tr>
              </thead>
              <tbody>
                {usage.summary.map((s) => (
                  <tr key={s.moduleKey}>
                    <td className="font-medium text-slate-800">{s.moduleName}</td>
                    <td>{s.count}</td>
                    <td>{s.totalPrice.toFixed(2)} RSD</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}

function ObradaIzvodaTab({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const { data: addresses, isLoading } = useQuery({
    queryKey: ["statement-import-addresses", companyId],
    queryFn: () => api.listStatementImportAddresses(companyId),
  });
  const { data: stats } = useQuery({
    queryKey: ["statement-processing-stats", companyId],
    queryFn: () => api.getStatementProcessingStats(companyId),
  });
  const { data: history } = useQuery({
    queryKey: ["statement-processing-history", companyId],
    queryFn: () => api.listStatementProcessingHistory(companyId),
  });

  const invalidateAddresses = () =>
    queryClient.invalidateQueries({ queryKey: ["statement-import-addresses", companyId] });

  const generate = useMutation({
    mutationFn: () => api.generateStatementImportAddress(companyId),
    onSuccess: invalidateAddresses,
  });
  const regenerate = useMutation({
    mutationFn: (addressId: string) => api.regenerateStatementImportAddress(addressId),
    onSuccess: invalidateAddresses,
  });

  const activeAddresses = addresses?.filter((a) => a.status === "ACTIVE") ?? [];

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-800">Mail adrese za obradu izvoda</h2>
          <Button size="sm" disabled={generate.isPending} onClick={() => generate.mutate()}>
            <Plus className="h-3.5 w-3.5" aria-hidden /> Nova adresa
          </Button>
        </div>
        {isLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : !addresses || addresses.length === 0 ? (
          <p className="text-sm text-slate-400">Firma još nema adresu za obradu izvoda.</p>
        ) : (
          <div className="space-y-1.5">
            {addresses.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-lg bg-app-bg px-3 py-2 text-sm">
                <div>
                  <span className="font-mono">{a.emailAddress}</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(a.emailAddress).catch(() => undefined)}
                    className="ml-2 align-middle text-slate-400 hover:text-brand"
                    title="Kopiraj adresu"
                  >
                    <Copy className="inline h-3.5 w-3.5" aria-hidden />
                  </button>
                  {a.label && <span className="ml-2 text-xs text-slate-400">({a.label})</span>}
                  <span
                    className={`ml-2 text-xs font-medium ${
                      a.status === "ACTIVE" ? "text-emerald-600" : "text-slate-400 line-through"
                    }`}
                  >
                    {a.status === "ACTIVE" ? "AKTIVAN" : "OPOZVAN"}
                  </span>
                </div>
                {a.status === "ACTIVE" && (
                  <button
                    onClick={() => {
                      if (window.confirm("Opozvati ovu adresu i generisati novu?")) {
                        regenerate.mutate(a.id);
                      }
                    }}
                    className="text-xs text-slate-400 hover:text-brand"
                  >
                    Regeneriši
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {activeAddresses.length === 0 && addresses && addresses.length > 0 && (
          <p className="mt-2 text-xs text-amber-600">
            Firma nema aktivnu adresu - sve su opozvane. Klikni "Nova adresa".
          </p>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-5 gap-3">
          <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
            <p className="text-xs text-slate-400">Primljeno danas</p>
            <p className="text-xl font-semibold text-slate-900">{stats.received}</p>
          </div>
          <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
            <p className="text-xs text-slate-400">Uspešno obrađeno</p>
            <p className="text-xl font-semibold text-emerald-600">{stats.success}</p>
          </div>
          <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
            <p className="text-xs text-slate-400">Greške</p>
            <p className="text-xl font-semibold text-red-600">{stats.failed}</p>
          </div>
          <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
            <p className="text-xs text-slate-400">Duplikati</p>
            <p className="text-xl font-semibold text-amber-600">{stats.duplicates}</p>
          </div>
          <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
            <p className="text-xs text-slate-400">Potrošnja danas</p>
            <p className="text-xl font-semibold text-slate-900">{formatMoney(stats.totalCost)}</p>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        <table>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Fajl</th>
              <th>Banka</th>
              <th>Transakcija</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {history?.map((h) => (
              <tr key={h.jobId}>
                <td className="text-slate-500">{new Date(h.createdAt).toLocaleString("sr-RS")}</td>
                <td className="font-medium text-slate-800">{h.filename ?? "—"}</td>
                <td>{h.bank ?? "—"}</td>
                <td>{h.transactionCount ?? "—"}</td>
                <td>
                  {h.status === "SUCCESS" ? (
                    <span className="text-emerald-600">Uspešno</span>
                  ) : (
                    <span className="cursor-help text-red-600" title={h.errorMessage ?? "Nema dodatnog opisa greške."}>
                      Neuspešno
                      {h.errorCode ? ` (${h.errorCode.replace(/^FINORA-/i, "")}${h.errorId ? ` / ${h.errorId}` : ""})` : ""}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(!history || history.length === 0) && (
          <p className="p-4 text-sm text-slate-400">Još nema evidentirane obrade izvoda.</p>
        )}
      </div>
    </div>
  );
}

import type { MobileUserAccessDto } from "@finora/types";

function UserMobileAccessRows({
  companyId,
  user,
  onToggleAccess,
  accessPending,
}: {
  companyId: string;
  user: MobileUserAccessDto;
  onToggleAccess: (mobileAppEnabled: boolean) => void;
  accessPending: boolean;
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  const { data: userModules, isLoading: userModulesLoading } = useQuery({
    queryKey: ["user-mobile-modules", companyId, user.userId],
    queryFn: () => api.getUserMobileModules(companyId, user.userId),
    enabled: expanded && user.mobileAppEnabled,
  });

  const toggleModule = useMutation({
    mutationFn: (input: { moduleId: string; allowed: boolean }) =>
      api.setUserMobileModule(companyId, user.userId, input.moduleId, input.allowed),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["user-mobile-modules", companyId, user.userId] }),
  });

  return (
    <>
      <tr>
        <td>
          <div className="font-medium text-slate-800">{user.fullName}</div>
          <div className="text-xs text-slate-400">{user.email}</div>
        </td>
        <td>{user.role}</td>
        <td>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={user.mobileAppEnabled}
                disabled={accessPending}
                onChange={(e) => onToggleAccess(e.target.checked)}
                className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
              />
              <span className="text-sm text-slate-600">
                {user.mobileAppEnabled ? "Prijava dozvoljena" : "Prijava zabranjena"}
              </span>
            </label>
            {user.mobileAppEnabled && (
              <button
                onClick={() => setExpanded((v) => !v)}
                className="text-xs text-brand hover:underline"
              >
                {expanded ? "Sakrij module" : "Podesi module za ovu licencu"}
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && user.mobileAppEnabled && (
        <tr>
          <td colSpan={3} className="bg-app-bg">
            {userModulesLoading ? (
              <p className="text-xs text-slate-400">Učitavanje...</p>
            ) : !userModules || userModules.length === 0 ? (
              <p className="text-xs text-slate-400">Firma nema nijedan aktivan modul za mobilni.</p>
            ) : (
              <div className="flex flex-wrap gap-3 py-1">
                {userModules.map((m) => (
                  <label key={m.moduleId} className="flex items-center gap-1.5 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={m.allowed}
                      disabled={toggleModule.isPending}
                      onChange={(e) => toggleModule.mutate({ moduleId: m.moduleId, allowed: e.target.checked })}
                      className="h-3.5 w-3.5 rounded border-border text-brand focus:ring-brand"
                    />
                    {m.name}
                  </label>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function FinoraMobileTab({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: devices, isLoading } = useQuery({
    queryKey: ["fcm-devices", companyId],
    queryFn: () => api.listFcmDevicesForCompany(companyId),
  });
  const { data: mobileUsers, isLoading: usersLoading } = useQuery({
    queryKey: ["mobile-users", companyId],
    queryFn: () => api.listMobileUsers(companyId),
  });
  const { data: licenseInfo } = useQuery({
    queryKey: ["mobile-license-info", companyId],
    queryFn: () => api.getMobileLicenseInfo(companyId),
  });

  const [pushTitle, setPushTitle] = useState("");
  const [pushBody, setPushBody] = useState("");

  const setAccess = useMutation({
    mutationFn: (input: { userId: string; mobileAppEnabled: boolean }) =>
      api.setUserMobileAccess(companyId, input.userId, input.mobileAppEnabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mobile-users", companyId] });
      queryClient.invalidateQueries({ queryKey: ["mobile-license-info", companyId] });
    },
  });

  const testPush = useMutation({
    mutationFn: (deviceId: string) =>
      api.sendTestPushToDevice(
        companyId,
        deviceId,
        pushTitle || pushBody ? { title: pushTitle, body: pushBody } : undefined,
      ),
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Pristup po korisniku (pod-nalogu)</h2>
        <p className="mb-3 text-xs text-slate-400">
          Nezavisno od modula "HAFiskal kasa aplikacija" (billing kapija za celu firmu, podešava se u
          Moduli tabu) - ovde odobravaš pristup pojedinačnom korisniku firme. Napomena: ovo je
          sve-ili-ništa dozvola za PRIJAVU u mobilnu aplikaciju - koji MODULI se vide na telefonu
          (Kasa/Unos robe/Prijem robe) i dalje određuje isti "Moduli" tab kao i za web (isti moduli za
          sve korisnike firme, nema još posebne kombinacije po korisniku).
        </p>
        {licenseInfo && (
          <p className="mb-3 text-xs font-medium text-slate-500">
            Licence: {licenseInfo.used}
            {licenseInfo.total != null ? ` / ${licenseInfo.total}` : ""} iskorišćeno
            {licenseInfo.total == null && " (nema ograničenja - podesi broj u Moduli tabu)"}
          </p>
        )}
        {setAccess.isError && (
          <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {setAccess.error instanceof Error ? setAccess.error.message : "Izmena nije uspela."}
          </p>
        )}
        {usersLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : !mobileUsers || mobileUsers.length === 0 ? (
          <p className="rounded-xl border border-border bg-surface p-4 text-sm text-slate-400 shadow-subtle">
            Firma još nema nijednog korisnika.
          </p>
        ) : (
          <div className="rounded-xl border border-border bg-surface shadow-subtle">
            <table>
              <thead>
                <tr>
                  <th>Korisnik</th>
                  <th>Rola</th>
                  <th>Pristup HAFiskal kasi</th>
                </tr>
              </thead>
              <tbody>
                {mobileUsers.map((u) => (
                  <UserMobileAccessRows
                    key={u.userId}
                    companyId={companyId}
                    user={u}
                    onToggleAccess={(mobileAppEnabled) => setAccess.mutate({ userId: u.userId, mobileAppEnabled })}
                    accessPending={setAccess.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h2 className="text-sm font-semibold text-slate-800">Registrovani uređaji</h2>
        <p className="text-xs text-slate-400">
          Uređaji na kojima je korisnik ove firme prijavljen u HAFiskal kasa aplikaciji i registrovao
          FCM token za push notifikacije.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
        <p className="mb-2 text-xs font-medium text-slate-500">
          Custom tekst za push (opciono - ostavi prazno za podrazumevanu test poruku)
        </p>
        <div className="grid grid-cols-2 gap-2">
          <input
            value={pushTitle}
            onChange={(e) => setPushTitle(e.target.value)}
            placeholder="Naslov"
            className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
          <input
            value={pushBody}
            onChange={(e) => setPushBody(e.target.value)}
            placeholder="Tekst poruke"
            className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : !devices || devices.length === 0 ? (
        <p className="rounded-xl border border-border bg-surface p-4 text-sm text-slate-400 shadow-subtle">
          Nijedan uređaj još nije registrovan za ovu firmu.
        </p>
      ) : (
        <div className="rounded-xl border border-border bg-surface shadow-subtle">
          <table>
            <thead>
              <tr>
                <th>Korisnik</th>
                <th>Platforma</th>
                <th>Token</th>
                <th>Poslednja aktivnost</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => {
                const online = Date.now() - new Date(d.lastActiveAt).getTime() < 5 * 60 * 1000;
                return (
                  <tr
                    key={d.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/finora-admin/companies/${companyId}/devices/${d.id}`)}
                  >
                    <td>
                      <div className="font-medium text-slate-800">{d.userName}</div>
                      <div className="text-xs text-slate-400">{d.userEmail}</div>
                    </td>
                    <td>{d.platform}</td>
                    <td>
                      {d.isActive ? (
                        <span className="text-emerald-600">Važi</span>
                      ) : (
                        <span className="text-slate-400">Istekao/opozvan</span>
                      )}
                      {" · "}
                      <span className={online ? "text-emerald-600" : "text-slate-400"}>
                        {online ? "Online" : "Offline"}
                      </span>
                    </td>
                    <td className="text-slate-500">{new Date(d.lastActiveAt).toLocaleString("sr-RS")}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={!d.isActive || testPush.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          testPush.mutate(d.id);
                        }}
                      >
                        Pošalji
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {testPush.isSuccess && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          Push poslat.
        </p>
      )}
      {testPush.isError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {testPush.error instanceof Error ? testPush.error.message : "Slanje nije uspelo."}
        </p>
      )}
    </div>
  );
}

const DURATION_LABELS: Record<ContractDurationOption, string> = {
  UNDEFINED: "Neodređeno",
  MONTHS_12: "12 meseci",
  MONTHS_24: "24 meseca",
};
const SLA_LABELS: Record<ContractSlaLevel, string> = {
  STANDARD: "Standard",
  PREMIUM: "Premium",
};
const STATUS_LABELS: Record<ContractDto["status"], { label: string; className: string }> = {
  AWAITING_UPLOAD: { label: "Čeka upload", className: "bg-amber-100 text-amber-700" },
  PENDING_APPROVAL: { label: "Čeka pregled admina", className: "bg-sky-100 text-sky-700" },
  APPROVED: { label: "Odobren", className: "bg-emerald-100 text-emerald-700" },
  REJECTED: { label: "Odbijen", className: "bg-red-100 text-red-700" },
};

/**
 * "Ugovor" tab na profilu firme (Finora Admin). NAPOMENA (01.10.2026): stara
 * "Podaci za ugovor (ContractSettings)" sekcija je uklonjena - skoro svi
 * placeholderi ugovora (38, vidi contract-placeholder.util.ts na backendu)
 * se SADA automatski mapiraju iz podataka koji već postoje u Finori
 * (profil firme, pretplata/paket, uređaji...); jedino što admin ručno
 * podešava je GLOBALNO (isti za svaki ugovor - Isporučilac, ESIR odobrenje,
 * rokovi) i živi na ContractTemplatesPage ("Finora Admin -> Šabloni
 * ugovora"), ne po firmi. Ovde ostaje samo dugme za generisanje/pregled
 * trenutnog izgleda ugovora (uvek dostupno, i pre nego što firma ikad
 * prihvati ugovor) i lista stvarno prihvaćenih ugovora firme.
 */
function UgovorTab({ companyId }: { companyId: string }) {
  return (
    <div className="space-y-4">
      <GenerisiUgovorSection companyId={companyId} />
      <ContractsListSection companyId={companyId} />
    </div>
  );
}

function GenerisiUgovorSection({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cena Premium podrške PO OVOM klijentu (01.10.2026, vidi razgovor "cena
  // supporta po korisniku") - svako ima specifične zahteve pa cena ne mora
  // biti ista za sve. Prazno polje = koristi se globalna cena (Šabloni
  // ugovora -> Globalni podaci za ugovor).
  const { data: contracts } = useQuery({
    queryKey: ["admin-contracts", companyId],
    queryFn: () => api.listAdminContracts(companyId),
  });
  const { data: providerSettings } = useQuery({
    queryKey: ["contract-provider-settings"],
    queryFn: () => api.getContractProviderSettings(),
  });
  const latestContract = contracts?.[0];

  const [priceInput, setPriceInput] = useState("");
  const [priceHydratedFor, setPriceHydratedFor] = useState<string | null>(null);
  useEffect(() => {
    if (latestContract && priceHydratedFor !== latestContract.id) {
      setPriceInput(latestContract.slaPremiumPriceOverride ?? "");
      setPriceHydratedFor(latestContract.id);
    }
  }, [latestContract, priceHydratedFor]);

  const [discountInput, setDiscountInput] = useState("");
  const [discountHydratedFor, setDiscountHydratedFor] = useState<string | null>(null);
  useEffect(() => {
    if (latestContract && discountHydratedFor !== latestContract.id) {
      setDiscountInput(latestContract.discountPercentOverride ?? "");
      setDiscountHydratedFor(latestContract.id);
    }
  }, [latestContract, discountHydratedFor]);

  const [durationInput, setDurationInput] = useState<ContractDurationOption>("UNDEFINED");
  const [durationHydratedFor, setDurationHydratedFor] = useState<string | null>(null);
  useEffect(() => {
    if (latestContract && durationHydratedFor !== latestContract.id) {
      setDurationInput(latestContract.durationOption);
      setDurationHydratedFor(latestContract.id);
    }
  }, [latestContract, durationHydratedFor]);

  // NAPOMENA (01.10.2026): "Generiši ugovor" sad radi DVA koraka - prvo
  // generateContract (kreira STVARAN Contract red ako firma još nema
  // nijedan - idempotentno, vidi backend; ili ažurira cenu Premium podrške
  // na postojećem) da ugovor postane vidljiv i ovde (ContractsListSection
  // ispod) i klijentu na "Moj ugovor", pa tek onda otvara PDF pregled koji
  // odražava taj (sad persistovan) ugovor.
  const handlePreview = async () => {
    setError(null);
    setIsGenerating(true);
    try {
      const trimmed = priceInput.trim();
      await api.generateContract(companyId, { slaPremiumPriceOverride: trimmed === "" ? null : Number(trimmed), durationOption: durationInput, discountPercentOverride: discountInput.trim() === "" ? null : Number(discountInput) });
      queryClient.invalidateQueries({ queryKey: ["admin-contracts", companyId] });
      const blobUrl = await api.fetchContractPreviewPdfBlobUrl(companyId);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err instanceof ApiError ? err.response.message : "Generisanje ugovora nije uspelo.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Ugovor</h2>
          <p className="mt-0.5 text-xs text-slate-400">
            Placeholderi se automatski popunjavaju iz profila firme, pretplate i paketa. Globalna polja (Isporučilac,
            ESIR odobrenje, rokovi) se podešavaju na Šabloni ugovora. Prvi klik kreira ugovor i čini ga dostupnim
            klijentu na "Moj ugovor".
          </p>
          <div className="mt-3">
            <label className="mb-1 block text-xs text-slate-500">Period ugovora</label>
            <select
              value={durationInput}
              onChange={(e) => setDurationInput(e.target.value as ContractDurationOption)}
              className="h-9 w-56 rounded-lg border border-border bg-white px-2 text-sm focus:border-brand focus:outline-none"
            >
              <option value="UNDEFINED">{DURATION_LABELS.UNDEFINED}</option>
              <option value="MONTHS_12">
                {DURATION_LABELS.MONTHS_12}
                {providerSettings ? ` (popust ${providerSettings.contractDiscount12Percent}%)` : ""}
              </option>
              <option value="MONTHS_24">
                {DURATION_LABELS.MONTHS_24}
                {providerSettings ? ` (popust ${providerSettings.contractDiscount24Percent}%)` : ""}
              </option>
            </select>
          </div>
          <div className="mt-3">
            <label className="mb-1 block text-xs text-slate-500">Popust za ovog klijenta (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={discountInput}
              onChange={(e) => setDiscountInput(e.target.value)}
              placeholder={
                providerSettings
                  ? `Podrazumevano: ${durationInput === "MONTHS_12" ? providerSettings.contractDiscount12Percent : durationInput === "MONTHS_24" ? providerSettings.contractDiscount24Percent : 0}`
                  : "Podrazumevano"
              }
              className="h-9 w-56 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
            <p className="mt-1 text-[11px] text-slate-400">
              Prazno = podrazumevani popust za izabrani period. Primenjuje se na mesečnu fakturu pretplate.
            </p>
          </div>
          <div className="mt-3">
            <label className="mb-1 block text-xs text-slate-500">
              Cena Premium podrške za ovog klijenta (RSD/mes.)
            </label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
              placeholder={providerSettings ? `Podrazumevano: ${providerSettings.slaPremiumPrice}` : "Podrazumevano"}
              className="h-9 w-56 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
            <p className="mt-1 text-[11px] text-slate-400">Prazno = koristi se globalna cena iz Šabloni ugovora.</p>
          </div>
        </div>
        <Button size="sm" disabled={isGenerating} onClick={handlePreview}>
          <FileText className="h-3.5 w-3.5" aria-hidden />
          {isGenerating ? "Generišem..." : "Generiši ugovor"}
        </Button>
      </div>
      {error && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
    </div>
  );
}

function ContractsListSection({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const { data: contracts, isLoading } = useQuery({
    queryKey: ["admin-contracts", companyId],
    queryFn: () => api.listAdminContracts(companyId),
  });

  const review = useMutation({
    mutationFn: (input: { id: string; decision: "APPROVED" | "REJECTED"; rejectionReason?: string }) =>
      api.reviewContract(input.id, { decision: input.decision, rejectionReason: input.rejectionReason }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-contracts", companyId] }),
  });

  if (isLoading) return <Skeleton className="h-32 w-full" />;

  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <h2 className="mb-3 text-sm font-semibold text-slate-800">Ugovori firme</h2>
      {!contracts || contracts.length === 0 ? (
        <p className="text-sm text-slate-400">Firma još nije prihvatila nijedan ugovor.</p>
      ) : (
        <div className="space-y-3">
          {contracts.map((c) => (
            <ContractCard
              key={c.id}
              contract={c}
              onReview={(decision, rejectionReason) => review.mutate({ id: c.id, decision, rejectionReason })}
              reviewPending={review.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ContractCard({
  contract,
  onReview,
  reviewPending,
}: {
  contract: ContractDto;
  onReview: (decision: "APPROVED" | "REJECTED", rejectionReason?: string) => void;
  reviewPending: boolean;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const status = STATUS_LABELS[contract.status];

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.className}`}>{status.label}</span>
        <span className="text-sm font-medium text-slate-800">{contract.template?.name ?? "Ugovor"}</span>
        <span className="text-xs text-slate-400">
          {DURATION_LABELS[contract.durationOption]} · {SLA_LABELS[contract.slaLevel]}
        </span>
        <a
          href={api.getContractPdfUrl(contract.id)}
          target="_blank"
          rel="noreferrer"
          className="ml-auto flex items-center gap-1 text-xs text-brand hover:underline"
        >
          <Download className="h-3.5 w-3.5" aria-hidden />
          PDF ugovora
        </a>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-500 sm:grid-cols-4">
        <span>Prihvaćen: {new Date(contract.acceptedAt).toLocaleDateString("sr-RS")}</span>
        <span>Rok za upload: {new Date(contract.uploadDeadlineAt).toLocaleDateString("sr-RS")}</span>
        <span>Lična karta: {contract.signatoryIdCardNumber ?? "— (klijent još nije uneo)"}</span>
        <span>Mesto izdavanja: {contract.signatoryIdCardIssuePlace ?? "—"}</span>
      </div>

      <div className="mt-2">
        <p className="mb-1 text-xs font-medium text-slate-500">Uploadovani dokumenti</p>
        {contract.documents.length === 0 ? (
          <p className="text-xs text-slate-400">Klijent još nije uploadovao potpisan primerak.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {contract.documents.map((d) => (
              <a
                key={d.id}
                href={d.fileData}
                download={d.fileName}
                className="flex items-center gap-1 rounded-lg border border-border bg-app-bg px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
              >
                <Download className="h-3 w-3" aria-hidden />
                {d.fileName}
              </a>
            ))}
          </div>
        )}
      </div>

      {contract.status === "REJECTED" && contract.rejectionReason && (
        <p className="mt-2 rounded-lg bg-red-50 px-2 py-1.5 text-xs text-red-700">
          Razlog odbijanja: {contract.rejectionReason}
        </p>
      )}

      {contract.status === "PENDING_APPROVAL" && (
        <div className="mt-3 border-t border-border pt-3">
          {rejecting ? (
            <div className="space-y-2">
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Razlog odbijanja (opciono)..."
                className="h-16 w-full rounded-lg border border-border p-2 text-xs focus:border-brand focus:outline-none"
              />
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => setRejecting(false)}>
                  Otkaži
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  disabled={reviewPending}
                  onClick={() => onReview("REJECTED", rejectionReason || undefined)}
                >
                  Potvrdi odbijanje
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" disabled={reviewPending} onClick={() => onReview("APPROVED")}>
                <Check className="h-3.5 w-3.5" aria-hidden />
                Odobri
              </Button>
              <Button size="sm" variant="secondary" disabled={reviewPending} onClick={() => setRejecting(true)}>
                <XIcon className="h-3.5 w-3.5" aria-hidden />
                Odbij
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
